import { type MenuVirtualTriggerProps, useLayoutContextSelector } from "@overmap-ai/blocks"
import {
	type Asset,
	type AssetType,
	type AssetTypeStatus,
	EMPTY_ARRAY,
	type ProcedureStep,
	type Stored,
} from "@overmap-ai/common"
import {
	selectAssetTypeById,
	selectAssetTypeIdentifiersOfAssetType,
	selectAssetTypeStatusesOfAssetType,
	selectLatestAssetTypeFieldsOfAssetType,
	selectLatestProcedureTypeFieldsOfProcedureType,
	selectProceduresOfProcedureInitiative,
	selectProcedureStepsOfProcedureType,
	selectUsersOfProject,
} from "@overmap-ai/core"
import { jade } from "@radix-ui/colors"
import { useNavigate } from "@tanstack/react-router"
import * as turf from "@turf/turf"
import type { Feature, Geometry, Point } from "geojson"
import type {
	CircleLayerSpecification,
	FillLayerSpecification,
	LineLayerSpecification,
	SymbolLayerSpecification,
} from "mapbox-gl"
import { memo, useCallback, useDeferredValue, useMemo, useState } from "react"
import isEqual from "react-fast-compare"
import { shallowEqual } from "react-redux"
import { useCustomCompareMemo } from "use-custom-compare"

import { useDeserializeFieldSchema } from "../../components/Form/hooks"
import { AssetMarkerContextMenu } from "../../components/MapContextMenus/AssetMarkerContextMenu"
import { AssetMapColorMode } from "../../enums/assets"
import { ProcedureStepCompletionStatus } from "../../enums/procedures"
import { useFilterManager, useFilters } from "../../filtering/hooks"
import { useAssetDataFilterSchema } from "../../filtering/schemas"
import { getProcedureDataFilterSchema } from "../../filtering/schemas/ProcedureDataFilterSchema"
import { useAssetData } from "../../hooks/assets"
import {
	useFieldSchemasAndConditionsByProcedureStepFields,
	useFieldSchemasAndConditionsOfProcedureSteps,
	useProcedureData,
} from "../../hooks/procedures"
import { SLIDE_OUT_IDS } from "../../layouts"
import { procedureStepCompletionStatusData } from "../../mappings/procedures"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import {
	selectActiveProcedureInitiativeOfAssetType,
	selectActiveProjectId,
	selectAssetGeometryFillOpacity,
	selectAssetGeometryStrokeOpacity,
	selectAssetMapColorMode,
	selectAssetsOfProjectAndAssetType,
	selectAssetTypeMapFiltersById,
	selectIsAssetTypeHidden,
	selectMapStyle,
	selectProcedureInitiativeMapFiltersById,
	selectShowAssetLabels,
} from "../../store/selectors"
import type { AssetData } from "../../typings"
import { getProcedureStepCompletionStatus } from "../../utils/procedures"
import { Layer, Source } from "../components"
import {
	EMPTY_FEATURE_COLLECTION,
	LINE_STRING_GEOMETRY_TYPE_FILTER,
	POINT_GEOMETRY_TYPE_FILTER,
	POLYGON_GEOMETRY_TYPE_FILTER,
	VISIBILITY_FILTER,
} from "../constants"
import { MapSlotLayerId, MapStyle } from "../enums"
import { useCurrentMapEvent, useCurrentMapLongPress } from "../hooks"
import { mapStyleColorMapping } from "../mappings"
import type { MapEventHandler, UUIDGeoJsonProperties } from "../typings"
import { computePointForGeometry, flattenGeometry } from "../utils"
import { RENDER_ICONS_THRESHOLD } from "./constants"
import { getAssetTypeLayerIds, getCircleLayerSizes } from "./utils"

type AssetFeatureProperties = UUIDGeoJsonProperties &
	Record<AssetMapColorMode, string> & {
		visibility: "visible" | "none"
	}

type AssetFeature<TGeometry extends Geometry = Geometry> = Feature<TGeometry, AssetFeatureProperties>

const CIRCLE_LAYER_FILTER = ["all", VISIBILITY_FILTER, POINT_GEOMETRY_TYPE_FILTER]

const LINE_LAYER_FILTER = ["all", VISIBILITY_FILTER, LINE_STRING_GEOMETRY_TYPE_FILTER]

const FILL_LAYER_FILTER = ["all", VISIBILITY_FILTER, POLYGON_GEOMETRY_TYPE_FILTER]

interface AssetLayerProps {
	assetTypeId: AssetType["uuid"]
	beforeAssetTypeId?: AssetType["uuid"]
}

export const AssetLayer = memo<AssetLayerProps>((props) => {
	const { assetTypeId, beforeAssetTypeId } = props

	const assetType = useAppSelector(selectAssetTypeById(assetTypeId))

	if (!assetType) {
		throw new Error(`Expected asset to have a valid asset type, got ${assetType}`)
	}

	const activeProjectId = useAppSelector(selectActiveProjectId)!
	const isHidden = useAppSelector(selectIsAssetTypeHidden(assetType.uuid))
	const navigate = useNavigate()
	const setOpen = useLayoutContextSelector((ctx) => ctx.setOpen)
	const assetMapColorMode = useAppSelector(selectAssetMapColorMode)
	const assetGeometryFillOpacity = useAppSelector(selectAssetGeometryFillOpacity)
	const assetGeometryStrokeOpacity = useAppSelector(selectAssetGeometryStrokeOpacity)
	const users = useShallowEqaulSelector(selectUsersOfProject(activeProjectId))
	const assets = useShallowEqaulSelector(selectAssetsOfProjectAndAssetType(activeProjectId, assetType.uuid))
	const assetData = useAssetData(assets)
	const assetTypeIdentifiers = useShallowEqaulSelector(selectAssetTypeIdentifiersOfAssetType(assetType.uuid))
	const assetTypeStatuses = useShallowEqaulSelector(selectAssetTypeStatusesOfAssetType(assetType.uuid))
	const latestAssetTypeFields = useAppSelector(selectLatestAssetTypeFieldsOfAssetType(assetType.uuid))
	const showAssetLabels = useAppSelector(selectShowAssetLabels(assetType.uuid))
	const serializedAssetTypeMapFilters = useAppSelector(selectAssetTypeMapFiltersById(assetType.uuid), shallowEqual)
	const activeProcedureInitiative = useAppSelector(selectActiveProcedureInitiativeOfAssetType(assetType.uuid))
	const procedureSteps = useShallowEqaulSelector(
		selectProcedureStepsOfProcedureType(activeProcedureInitiative?.procedure_type ?? ""),
	)
	const procedureTypeFields = useAppSelector(
		selectLatestProcedureTypeFieldsOfProcedureType(activeProcedureInitiative?.procedure_type ?? ""),
	)
	const procedureStepFieldSchemas = useFieldSchemasAndConditionsOfProcedureSteps(procedureSteps)
	const fieldSchemasAndConditionsByProcedureStepFields = useFieldSchemasAndConditionsByProcedureStepFields()
	const serializedActiveProcedureInitiativeFilters = useAppSelector(
		selectProcedureInitiativeMapFiltersById(activeProcedureInitiative?.uuid ?? ""),
	)
	const procedures = useShallowEqaulSelector(
		selectProceduresOfProcedureInitiative(activeProcedureInitiative?.uuid ?? ""),
	)
	const procedureData = useProcedureData(procedures)

	const [assetContextMenuVirtualElement, setAssetContextMenuVirtualElement] =
		useState<MenuVirtualTriggerProps["virtualElement"]>(null)
	const [assetMarkerContextMenuAssetId, setAssetMarkerContextMenuAssetId] = useState<string | null>(null)
	const mapStyle = useAppSelector(selectMapStyle)
	const mapStyleColor = mapStyleColorMapping[mapStyle]

	const {
		assetSymbolLayerId,
		assetSymbolBackgroundLayerId,
		assetCircleLayerId,
		assetLineLayerId,
		assetOuterLineLayerId,
		assetFillLayerId,
		assetFillLineLayerId,
	} = getAssetTypeLayerIds(assetTypeId)

	const labelLayerId = `${assetTypeId}-label`

	const interactiveLayers = useMemo(
		() => [
			assetSymbolLayerId,
			assetSymbolBackgroundLayerId,
			assetCircleLayerId,
			assetLineLayerId,
			assetOuterLineLayerId,
			assetFillLineLayerId,
			assetFillLayerId,
		],
		[
			assetCircleLayerId,
			assetFillLayerId,
			assetFillLineLayerId,
			assetLineLayerId,
			assetOuterLineLayerId,
			assetSymbolBackgroundLayerId,
			assetSymbolLayerId,
		],
	)

	const assetTypeFieldSchema = useDeserializeFieldSchema(latestAssetTypeFields?.fields ?? EMPTY_ARRAY)

	const assetDataFilterSchema = useAssetDataFilterSchema({
		assetTypeIdentifiers,
		assetTypeFieldSchema,
		assetTypeStatuses,
	})

	const assetDataFilters = useMemo(() => {
		return assetDataFilterSchema.deserializeFilters(serializedAssetTypeMapFilters)
	}, [assetDataFilterSchema, serializedAssetTypeMapFilters])

	const assetDataFilterManager = useFilterManager<AssetData>({
		filterSchema: assetDataFilterSchema,
		filters: assetDataFilters,
	})

	const filteredAssetData = useMemo(() => {
		if (isHidden) return []

		return assetDataFilterManager.applyFilters(assetDataFilters, assetData)
	}, [assetData, assetDataFilterManager, assetDataFilters, isHidden])

	const procedureTypeFieldSchema = useDeserializeFieldSchema(procedureTypeFields?.fields ?? EMPTY_ARRAY)

	const procedureDataFilterSchema = useMemo(() => {
		return getProcedureDataFilterSchema({
			procedureInitiatives: EMPTY_ARRAY,
			procedureTypeFieldSchema,
			procedureSteps,
			procedureStepFieldSchemasAndConditions: procedureStepFieldSchemas,
			users,
		})
	}, [procedureTypeFieldSchema, procedureSteps, procedureStepFieldSchemas, users])

	const procedureDataFilters = useMemo(() => {
		return procedureDataFilterSchema.deserializeFilters(serializedActiveProcedureInitiativeFilters)
	}, [procedureDataFilterSchema, serializedActiveProcedureInitiativeFilters])

	const procedureDataFilterManager = useFilterManager({
		filterSchema: procedureDataFilterSchema,
		filters: procedureDataFilters,
	})

	const filteredProcedureData = useFilters({
		filterManager: procedureDataFilterManager,
		filters: procedureDataFilters,
		data: procedureData,
	})

	const handleMapClick = useCallback<MapEventHandler<"click">>(
		(event) => {
			const { target, point } = event

			const features = target.queryRenderedFeatures(point, {
				layers: interactiveLayers,
			})

			const feature = features[0]

			if (!feature || !feature.properties?.uuid) return

			event.preventDefault()
			const assetId = feature.properties.uuid as string

			void navigate({
				to: "/$organizationId/$projectId",
				from: "/$organizationId/$projectId",
				search: { asset: assetId },
			})
			setOpen(SLIDE_OUT_IDS.RIGHT, true)
		},
		[interactiveLayers, navigate, setOpen],
	)

	const handleMapContextMenu = useCallback<MapEventHandler<"contextmenu">>(
		(event) => {
			const { target, point, originalEvent } = event

			const features = target.queryRenderedFeatures(point, {
				layers: interactiveLayers,
			})

			const feature = features[0]

			if (!feature || !feature.properties?.uuid) return

			const assetId = feature.properties.uuid as string

			const { clientX, clientY } = originalEvent

			event.preventDefault()

			setAssetMarkerContextMenuAssetId(assetId)
			setAssetContextMenuVirtualElement({
				getBoundingClientRect() {
					return {
						width: 0,
						height: 0,
						x: 0,
						y: 0,
						top: clientY,
						left: clientX,
						right: 0,
						bottom: 0,
					}
				},
			})
		},
		[interactiveLayers],
	)

	const handleMapLongPress = useCallback<MapEventHandler<"touchstart">>(
		(event) => {
			const { target, point, originalEvent } = event

			const features = target.queryRenderedFeatures(point, {
				layers: interactiveLayers,
			})

			const feature = features[0]

			if (!feature || !feature.properties?.uuid) return

			const assetId = feature.properties.uuid as string

			const { clientX, clientY } = originalEvent.touches[0]

			event.preventDefault()

			setAssetMarkerContextMenuAssetId(assetId)
			setAssetContextMenuVirtualElement({
				getBoundingClientRect() {
					return {
						width: 0,
						height: 0,
						x: 0,
						y: 0,
						top: clientY,
						left: clientX,
						right: 0,
						bottom: 0,
					}
				},
			})
		},
		[interactiveLayers],
	)

	const handleCloseContextMenu = useCallback(() => {
		setAssetMarkerContextMenuAssetId(null)
		setAssetContextMenuVirtualElement(null)
	}, [])

	const handleAssetMarkerContextDelete = useCallback(async () => {
		await navigate({
			to: "/$organizationId/$projectId",
			from: "/$organizationId/$projectId",
			search: { asset: undefined },
			replace: true,
		})
		setOpen(SLIDE_OUT_IDS.RIGHT, false)
		handleCloseContextMenu()
	}, [handleCloseContextMenu, navigate, setOpen])

	useCurrentMapEvent("zoomstart", handleCloseContextMenu)
	useCurrentMapEvent("click", handleMapClick)
	useCurrentMapEvent("contextmenu", handleMapContextMenu)
	useCurrentMapLongPress(handleMapLongPress)

	const assetVisibilityMapping = useCustomCompareMemo(
		() => {
			const ret: Record<Asset["uuid"], "visible" | "none"> = {}

			const filteredAssetUuids = filteredAssetData.map(({ asset }) => asset.uuid)
			const filteredAssetUuidsSet = new Set(filteredAssetUuids)

			const filteredProcedureAssetUuids = filteredProcedureData.map(({ procedure }) => procedure.asset)
			const filteredProcedureAssetUuidsSet = new Set(filteredProcedureAssetUuids)

			for (const { asset } of assetData) {
				const visible =
					filteredAssetUuidsSet.has(asset.uuid) &&
					(activeProcedureInitiative?.uuid ? filteredProcedureAssetUuidsSet.has(asset.uuid) : true)

				ret[asset.uuid] = visible ? "visible" : "none"
			}

			return ret
		},
		[assetData, filteredAssetData, filteredProcedureData, activeProcedureInitiative?.uuid],
		(prev, next) => {
			const prevAssetIds = prev[0].map(({ asset: { uuid } }) => uuid)
			const nextAssetIds = next[0].map(({ asset: { uuid } }) => uuid)

			const prevFilteredAssetIds = prev[1].map(({ asset: { uuid } }) => uuid)
			const nextFilteredAssetIds = next[1].map(({ asset: { uuid } }) => uuid)

			const prevProcedureAssetIds = prev[2].map(({ procedure: { asset } }) => asset)
			const nextProcedureAssetIds = next[2].map(({ procedure: { asset } }) => asset)

			return (
				shallowEqual(prevAssetIds, nextAssetIds) &&
				shallowEqual(prevFilteredAssetIds, nextFilteredAssetIds) &&
				shallowEqual(prevProcedureAssetIds, nextProcedureAssetIds) &&
				prev[3] === next[3]
			)
		},
	)

	const assetTypeStatusColorMapping = useCustomCompareMemo(
		() => {
			const ret: Record<Asset["uuid"], string> = {}

			const statusColorMapping: Record<AssetTypeStatus["uuid"], string> = {}

			for (const { uuid, color } of assetTypeStatuses) {
				statusColorMapping[uuid] = color
			}

			for (const { uuid, status } of assets) {
				ret[uuid] = statusColorMapping[status]
			}

			return ret
		},
		[assets, assetTypeStatuses],
		(prev, next) => {
			const prevAssets = prev[0].map(({ uuid, status }) => ({ uuid, status }))
			const nextAssets = next[0].map(({ uuid, status }) => ({ uuid, status }))

			const prevStatuses = prev[1].map(({ uuid, color }) => ({ uuid, color }))
			const nextStatuses = next[1].map(({ uuid, color }) => ({ uuid, color }))

			return isEqual(prevAssets, nextAssets) && isEqual(prevStatuses, nextStatuses)
		},
	)

	const procedureStepCompletionsByAsset = useMemo(() => {
		const ret: Record<Asset["uuid"], Record<ProcedureStep["uuid"], ProcedureStepCompletionStatus> | undefined> = {}

		for (const { procedure, procedureStepFieldValues } of procedureData) {
			if (!ret[procedure.asset]) ret[procedure.asset] = {}

			for (const procedureStep of procedureSteps) {
				const fieldValues = procedureStepFieldValues[procedureStep.uuid]

				let status: ProcedureStepCompletionStatus | undefined

				if (!fieldValues || !(fieldValues.fields_revision in fieldSchemasAndConditionsByProcedureStepFields)) {
					status = ProcedureStepCompletionStatus.INCOMPLETE
				} else {
					const [fieldSchema, conditionManager] =
						fieldSchemasAndConditionsByProcedureStepFields[fieldValues.fields_revision]

					status = getProcedureStepCompletionStatus(
						fieldSchema,
						conditionManager,
						fieldValues.values,
						!fieldValues.published_at,
					)
				}

				ret[procedure.asset]![procedureStep.uuid] = status
			}
		}

		return ret
	}, [procedureData, procedureSteps, fieldSchemasAndConditionsByProcedureStepFields])

	const [firstIncompleteStepColorMapping, lastCompletedStepColorMapping] = useCustomCompareMemo(
		() => {
			const ret1: Record<Asset["uuid"], string> = {}
			const ret2: Record<Asset["uuid"], string> = {}

			const reversedProcedureSteps: Stored<ProcedureStep>[] = procedureSteps.toReversed()

			for (const { asset } of assetData) {
				if (!(asset.uuid in procedureStepCompletionsByAsset)) {
					ret1[asset.uuid] = mapStyleColor
					ret2[asset.uuid] = mapStyleColor
					continue
				}

				const procedureStepCompletionStatuses = procedureStepCompletionsByAsset[asset.uuid]!

				// FIRST INCOMPLETE

				let firstIncompleteColor = jade.jade9

				const firstIncompleteProcedureStep = procedureSteps.find(
					({ uuid }) => procedureStepCompletionStatuses[uuid] !== ProcedureStepCompletionStatus.COMPLETE,
				)

				if (firstIncompleteProcedureStep) {
					firstIncompleteColor = firstIncompleteProcedureStep.color
				}

				// LAST COMPLETED

				let lastCompletedColor = mapStyleColor

				const lastCompletedProcedureStep = reversedProcedureSteps.find(
					({ uuid }) => procedureStepCompletionStatuses[uuid] !== ProcedureStepCompletionStatus.INCOMPLETE,
				)

				if (lastCompletedProcedureStep) {
					const status = procedureStepCompletionStatuses[lastCompletedProcedureStep.uuid]

					switch (status) {
						case ProcedureStepCompletionStatus.COMPLETE:
							lastCompletedColor = lastCompletedProcedureStep.color
							break
						case ProcedureStepCompletionStatus.FAILED:
							lastCompletedColor = procedureStepCompletionStatusData[status].color
							break
					}
				}

				ret1[asset.uuid] = firstIncompleteColor
				ret2[asset.uuid] = lastCompletedColor
			}

			return [ret1, ret2]
		},
		[assetData, procedureSteps, procedureStepCompletionsByAsset, mapStyleColor],
		(prev, next) => {
			const prevAssetIds = prev[0].map(({ asset: { uuid } }) => uuid)
			const nextAssetIds = next[0].map(({ asset: { uuid } }) => uuid)

			const prevProcedureSteps = prev[1].map(({ uuid, color, order }) => ({
				uuid,
				color,
				order,
			}))

			const nextProcedureSteps = next[1].map(({ uuid, color, order }) => ({
				uuid,
				color,
				order,
			}))

			return (
				shallowEqual(prevAssetIds, nextAssetIds) &&
				isEqual(prevProcedureSteps, nextProcedureSteps) &&
				isEqual(prev[2], next[2]) &&
				prev[3] === next[3]
			)
		},
	)

	/** Current solution for large number of asset features, defer the updating of the stage color mappings to prevent UI blocking */
	const defferedFirstIncompleteStepColorMapping = useDeferredValue(firstIncompleteStepColorMapping)
	const defferedLastCompletedStepColorMapping = useDeferredValue(lastCompletedStepColorMapping)
	const defferedAssetTypeStatusColorMapping = useDeferredValue(assetTypeStatusColorMapping)
	const defferedAssetVisibilityMapping = useDeferredValue(assetVisibilityMapping)

	const renderIcons = assetData.length <= RENDER_ICONS_THRESHOLD
	const renderLabels = showAssetLabels

	const assetFeatures = useCustomCompareMemo(
		() => {
			const assetFeatures: AssetFeature[] = []

			for (const { asset } of assetData) {
				const uuid = asset.uuid
				const firstIncompleteStepColor = defferedFirstIncompleteStepColorMapping[asset.uuid]
				const lastCompletedStepColor = defferedLastCompletedStepColorMapping[asset.uuid]
				const assetTypeStatusColors = defferedAssetTypeStatusColorMapping[asset.uuid]
				const visibility = defferedAssetVisibilityMapping[asset.uuid]

				assetFeatures.push(
					turf.feature(asset.geometry, {
						uuid,
						visibility,
						[AssetMapColorMode.STATUS]: assetTypeStatusColors,
						[AssetMapColorMode.FIRST_INCOMPLETE_STEP]: firstIncompleteStepColor,
						[AssetMapColorMode.LAST_COMPLETED_STEP]: lastCompletedStepColor,
					}),
				)
			}

			return turf.featureCollection(assetFeatures)
		},
		[
			assetData,
			// Step color mappings
			defferedFirstIncompleteStepColorMapping,
			defferedLastCompletedStepColorMapping,
			// Status color mappings
			defferedAssetTypeStatusColorMapping,
			// Visibility mappings
			defferedAssetVisibilityMapping,
		],
		(prev, next) => {
			const prevAssets = prev[0].map(({ asset: { uuid, geometry } }) => ({ uuid, geometry }))
			const curAssets = next[0].map(({ asset: { uuid, geometry } }) => ({ uuid, geometry }))
			return (
				isEqual(prevAssets, curAssets) &&
				shallowEqual(prev[1], next[1]) &&
				shallowEqual(prev[2], next[2]) &&
				shallowEqual(prev[3], next[3]) &&
				shallowEqual(prev[4], next[4])
			)
		},
	)

	const assetCentroidFeatures = useCustomCompareMemo(
		() => {
			const assetCentroidFeatures: Feature<Point, Pick<AssetFeatureProperties, "uuid" | "visibility">>[] = []

			for (const { asset } of assetData) {
				const uuid = asset.uuid
				const visibility = defferedAssetVisibilityMapping[asset.uuid]

				for (const geometry of flattenGeometry(asset.geometry)) {
					assetCentroidFeatures.push(
						turf.feature(computePointForGeometry(geometry), {
							uuid,
							visibility,
							label: asset.label,
						}),
					)
				}
			}

			return turf.featureCollection(assetCentroidFeatures)
		},
		[assetData, defferedAssetVisibilityMapping, assetType],
		(prev, next) => {
			const prevAssets = prev[0].map(({ asset: { uuid, geometry, label } }) => ({ uuid, geometry, label }))
			const curAssets = next[0].map(({ asset: { uuid, geometry, label } }) => ({ uuid, geometry, label }))

			return isEqual(prevAssets, curAssets) && shallowEqual(prev[1], next[1])
		},
	)

	// SYMBOL LAYERS

	const { symbolIconSize, symbolRadius, circleRadius, circleStroke } = useMemo(
		() => getCircleLayerSizes(renderIcons),
		[renderIcons],
	)

	const symbolLayerLayout = useMemo(() => {
		return {
			"icon-image": [
				"image",
				assetType.icon,
				{
					// NOTE: small hack for Mapbox to use the correct color for the icon, when non sdf icon color is supporting
					// use the "icon-color" paint property to set the color of the icon
					params: {
						"color-1": assetType.color,
					},
				},
			],
			"icon-size": symbolIconSize,
			"icon-allow-overlap": true,
			"icon-ignore-placement": true,
			"icon-optional": true,
		} satisfies SymbolLayerSpecification["layout"]
	}, [assetType.color, assetType.icon, symbolIconSize])

	const symbolBackgroundLayerPaint = useMemo(() => {
		return {
			"circle-radius": symbolRadius,
			"circle-color": "#000",
		} satisfies CircleLayerSpecification["paint"]
	}, [symbolRadius])

	const labelLayerLayout = useMemo(() => {
		return {
			"text-field": ["get", "label"],
			"text-size": 12,
			"text-anchor": "top",
			"text-allow-overlap": true,
			"text-rotate": 0,
			"text-ignore-placement": true,
			"text-offset": [0, 1],
		} satisfies SymbolLayerSpecification["layout"]
	}, [])

	const labelLayerPaint = useMemo(() => {
		return {
			"text-color": mapStyleColorMapping[MapStyle.DARK],
			"text-halo-color": mapStyleColorMapping[MapStyle.LIGHT],
			"text-halo-width": 1.5,
		} satisfies SymbolLayerSpecification["paint"]
	}, [])

	// ASSET BASE LAYER

	const assetCircleLayerPaint = useMemo(() => {
		return {
			"circle-radius": circleRadius,
			"circle-color": ["get", assetMapColorMode],
			"circle-opacity": assetGeometryFillOpacity,
			"circle-stroke-width": circleStroke,
			"circle-stroke-color": mapStyleColor,
			"circle-stroke-opacity": assetGeometryStrokeOpacity,
		} satisfies CircleLayerSpecification["paint"]
	}, [
		assetMapColorMode,
		circleRadius,
		circleStroke,
		assetGeometryFillOpacity,
		mapStyleColor,
		assetGeometryStrokeOpacity,
	])

	const assetLineLayerPaint = useMemo(() => {
		return {
			"line-width": 4,
			"line-color": ["get", assetMapColorMode],
			"line-opacity": assetGeometryFillOpacity,
		} satisfies LineLayerSpecification["paint"]
	}, [assetMapColorMode, assetGeometryFillOpacity])

	const assetOuterLineLayerPaint = useMemo(() => {
		return {
			"line-width": 8,
			"line-color": mapStyleColor,
			"line-opacity": assetGeometryStrokeOpacity,
		} satisfies LineLayerSpecification["paint"]
	}, [mapStyleColor, assetGeometryStrokeOpacity])

	const assetFillLayerPaint = useMemo(() => {
		return {
			"fill-color": ["get", assetMapColorMode],
			"fill-opacity": assetGeometryFillOpacity,
		} satisfies FillLayerSpecification["paint"]
	}, [assetMapColorMode, assetGeometryFillOpacity])

	const assetFillLineLayerPaint = useMemo(() => {
		return {
			"line-width": 2,
			"line-color": mapStyleColor,
			"line-opacity": assetGeometryStrokeOpacity,
			"line-offset": -1,
		} satisfies LineLayerSpecification["paint"]
	}, [mapStyleColor, assetGeometryStrokeOpacity])

	return (
		<>
			<Source type="geojson" data={EMPTY_FEATURE_COLLECTION}>
				<Layer type="slot" id={assetType.uuid} beforeId={beforeAssetTypeId ?? MapSlotLayerId.ASSETS} />
			</Source>

			<Source type="geojson" data={assetCentroidFeatures} promoteId="uuid" minzoom={16}>
				<Layer
					id={assetSymbolLayerId}
					type="symbol"
					layout={symbolLayerLayout}
					beforeId={assetType.uuid}
					filter={["all", VISIBILITY_FILTER, renderIcons]}
				/>

				<Layer
					id={labelLayerId}
					type="symbol"
					layout={labelLayerLayout}
					paint={labelLayerPaint}
					beforeId={assetType.uuid}
					filter={["all", VISIBILITY_FILTER, renderLabels]}
				/>

				<Layer
					id={assetSymbolBackgroundLayerId}
					type="circle"
					paint={symbolBackgroundLayerPaint}
					beforeId={assetSymbolLayerId}
					filter={["all", VISIBILITY_FILTER, renderIcons]}
				/>
			</Source>

			<Source type="geojson" data={assetFeatures} promoteId="uuid">
				<Layer
					id={assetCircleLayerId}
					type="circle"
					paint={assetCircleLayerPaint}
					beforeId={assetSymbolBackgroundLayerId}
					filter={CIRCLE_LAYER_FILTER}
				/>

				<Layer
					id={assetLineLayerId}
					type="line"
					paint={assetLineLayerPaint}
					beforeId={assetSymbolBackgroundLayerId}
					filter={LINE_LAYER_FILTER}
				/>
				<Layer
					id={assetOuterLineLayerId}
					type="line"
					paint={assetOuterLineLayerPaint}
					beforeId={assetLineLayerId}
					filter={LINE_LAYER_FILTER}
				/>

				<Layer
					id={assetFillLineLayerId}
					type="line"
					paint={assetFillLineLayerPaint}
					beforeId={assetSymbolBackgroundLayerId}
					filter={FILL_LAYER_FILTER}
				/>
				<Layer
					id={assetFillLayerId}
					type="fill"
					paint={assetFillLayerPaint}
					beforeId={assetFillLineLayerId}
					filter={FILL_LAYER_FILTER}
				/>
			</Source>

			{assetMarkerContextMenuAssetId !== null && (
				<AssetMarkerContextMenu
					assetId={assetMarkerContextMenuAssetId}
					virtualElement={assetContextMenuVirtualElement}
					onDelete={handleAssetMarkerContextDelete}
				/>
			)}
		</>
	)
})
AssetLayer.displayName = "AssetLayer"
